import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save } from "lucide-react";

const EMPTY = {
  api_key: "",
  auth_domain: "",
  project_id: "",
  app_id: "",
  messaging_sender_id: "",
  storage_bucket: "",
};

const FIELDS = [
  ["api_key", "API key", "AIza…"],
  ["auth_domain", "Auth domain", "your-project.firebaseapp.com"],
  ["project_id", "Project ID", "your-project"],
  ["messaging_sender_id", "Messaging sender ID", "1234567890"],
  ["app_id", "App ID", "1:123:web:abc"],
  ["storage_bucket", "Storage bucket (optional)", "your-project.appspot.com"],
];

export default function AdminFirebaseOtp() {
  const [active, setActive] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/merchant/config/firebase-otp").then(({ data }) => {
      setActive(Boolean(data.active));
      setCfg({ ...EMPTY, ...(data.firebase_otp || {}) });
    }).catch((e) => toast.error(extractErr(e)));
  }, []);

  if (!cfg) return <div className="text-slate-500 p-4">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-3xl space-y-4 px-1">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Firebase OTP</strong> from the App Store to add mobile OTP on signup and login.</Card>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/config/firebase-otp", cfg);
      setCfg({ ...EMPTY, ...(data.firebase_otp || {}) });
      toast.success(data.configured ? "Firebase OTP saved. It is live on your store." : "Saved. Fill every required field to turn OTP on.");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="fotp-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Firebase OTP</h1>
        <p className="text-sm text-slate-500 mt-1">Your Firebase project sends the SMS. Signup asks for a mobile code. Sign-in opens on mobile OTP, with a link to the email login you already have.</p>
      </div>

      <Card className="p-4 sm:p-6 space-y-4 border-slate-200/70 text-sm text-slate-700 leading-relaxed" data-testid="fotp-instructions">
        <p className="font-semibold text-slate-900">How to set this up</p>
        <ol className="list-decimal pl-5 space-y-2">
          <li>Open <span className="font-medium">console.firebase.google.com</span> and create a project, or pick the one you already use for this brand. Phone SMS needs the <span className="font-medium">Blaze</span> (pay as you go) plan. You can set a budget alert so it does not run away.</li>
          <li>Go to <span className="font-medium">Build → Authentication → Sign-in method</span>. Enable <span className="font-medium">Phone</span>. Save.</li>
          <li>Open <span className="font-medium">Authentication → Settings → Authorized domains</span>. Add <span className="font-medium">merastartup.in</span>, your custom store domain (and its www host), and any preview domain you use. <span className="font-medium">localhost</span> and <span className="font-medium">your-project.firebaseapp.com</span> are already allowed. The website OTP will fail until the domain the shopper is on is in this list.</li>
          <li>Go to <span className="font-medium">Project settings</span> (gear) → <span className="font-medium">Your apps</span>. Add a <span className="font-medium">Web</span> app if you do not have one. Copy the config object: apiKey, authDomain, projectId, storageBucket, messagingSenderId, appId. Paste them in the form below. These values are public in the website; they are not a server secret. Do not paste a service-account JSON file here.</li>
          <li>For the Android app built in <span className="font-medium">App builder</span>: in Firebase, add an Android app with the same package id shown in App builder. The app still uses this web config inside its login screen, so the auth domain above is enough for OTP. You do not need a separate google-services.json in MeraStartup.</li>
          <li>Optional: under Phone, add test numbers while you are trying it, so Firebase does not send a real SMS or bill you.</li>
          <li>Save below. When every required field is filled, the storefront signup form gains a mobile OTP step, and customer login opens on “Login with mobile”. A “Login with email” link opens the current email and password screen. Switch this addon off in the App Store to hide both.</li>
          <li>India numbers are sent as +91 followed by the 10 digits the shopper types. Firebase must be allowed to send SMS to that country (Phone settings → region policy).</li>
        </ol>
      </Card>

      <Card className="p-4 sm:p-6 space-y-4 border-slate-200/70">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([key, label, placeholder]) => (
            <div key={key} className={key === "app_id" ? "sm:col-span-2" : ""}>
              <Label htmlFor={`fotp-${key}`}>{label}</Label>
              <Input
                id={`fotp-${key}`}
                className="mt-1 font-mono text-sm"
                data-testid={`fotp-${key}`}
                placeholder={placeholder}
                autoComplete="off"
                value={cfg[key] || ""}
                onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
              />
            </div>
          ))}
        </div>
        <Button type="button" onClick={save} disabled={saving} data-testid="fotp-save" className="w-full sm:w-auto">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Firebase config"}
        </Button>
      </Card>
    </div>
  );
}
